import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'

const ScreenSaverContext = createContext()

export const useScreenSaver = () => {
  const context = useContext(ScreenSaverContext)
  if (!context) {
    throw new Error('useScreenSaver must be used within a ScreenSaverProvider')
  }
  return context
}

export const ScreenSaverProvider = ({ children }) => {
  const [isScreenSaverActive, setIsScreenSaverActive] = useState(false)
  const [lastActivity, setLastActivity] = useState(Date.now())

  // Screen saver timeout in milliseconds (5 minutes = 300000ms)
  const SCREEN_SAVER_TIMEOUT = 5 * 60 * 1000

  // Update last activity time
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now())
    if (isScreenSaverActive) {
      setIsScreenSaverActive(false)
    }
  }, [isScreenSaverActive])

  // Handle user activity events
  const handleUserActivity = useCallback(() => {
    updateActivity()
  }, [updateActivity])

  // Effect to monitor for inactivity
  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now()
      const timeSinceLastActivity = now - lastActivity

      if (timeSinceLastActivity >= SCREEN_SAVER_TIMEOUT && !isScreenSaverActive) {
        setIsScreenSaverActive(true)
      }
    }

    // Check every 30 seconds
    const interval = setInterval(checkInactivity, 30000)

    return () => clearInterval(interval)
  }, [lastActivity, isScreenSaverActive, SCREEN_SAVER_TIMEOUT])

  // Effect to add global event listeners for user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ]

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true)
    })

    // Cleanup event listeners
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true)
      })
    }
  }, [handleUserActivity])

  // Manual control functions
  const activateScreenSaver = useCallback(() => {
    setIsScreenSaverActive(true)
  }, [])

  const deactivateScreenSaver = useCallback(() => {
    setIsScreenSaverActive(false)
    updateActivity()
  }, [updateActivity])

  const value = {
    isScreenSaverActive,
    activateScreenSaver,
    deactivateScreenSaver,
    updateActivity,
    lastActivity
  }

  return (
    <ScreenSaverContext.Provider value={value}>
      {children}
    </ScreenSaverContext.Provider>
  )
}

export default ScreenSaverProvider